import type { PropsWithChildren } from "react";
import { AppBar, Box, Skeleton, Toolbar, Typography } from "@mui/material";
import { AeriosIQLogo, color } from "@aeriosiq/ui";
import { useTenantContext } from "./TenantGate.js";

/**
 * Stripped-down chrome for /login + /auth/callback. No sidebar, header
 * shows the tenant logo + name (or the platform mark + "AeriosIQ"
 * when the host is unknown, per UX brief §State 5).
 */
export function LoginChrome({ children }: PropsWithChildren) {
  const tenantQuery = useTenantContext();
  const tenant = tenantQuery.tenant;
  const isPending = tenantQuery.isPending;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <AppBar
        position="static"
        elevation={0}
        sx={{
          bgcolor: color.light.neutralDeep,
          color: "common.white",
          borderBottom: 1,
          borderColor: "rgba(255,255,255,0.08)",
        }}
      >
        <Toolbar variant="dense" sx={{ gap: 2, minHeight: 56 }}>
          {isPending ? (
            <>
              <Skeleton variant="rectangular" width={28} height={28} />
              <Skeleton
                variant="text"
                width={140}
                sx={{ bgcolor: "rgba(255,255,255,0.18)" }}
              />
            </>
          ) : tenant?.brandingOverride?.logoUrl ? (
            <>
              {/*
                Decorative — the displayName Typography next to it carries the
                same information for AT users. The hero logo in the card body
                is the role="img" surface that the Playwright suite asserts
                against (single-match), so this image's alt is intentionally
                empty (axe-clean: presentational role).
              */}
              <Box
                component="img"
                src={tenant.brandingOverride.logoUrl}
                alt=""
                role="presentation"
                sx={{ height: 28, width: "auto" }}
              />
              <Typography variant="subtitle1">{tenant.displayName}</Typography>
            </>
          ) : tenant ? (
            <Typography variant="subtitle1">{tenant.displayName}</Typography>
          ) : (
            // Unknown-tenant or network-error fallback — UX brief §State 5/6.
            // Both states show the same neutral AeriosIQ brand identity,
            // since we have no resolved tenant to draw on in either case.
            // 2026-07-08: designer lockup (ARIOS2.1.svg) replaces the
            // mark + live-text pair so the brand reads identically across
            // the authenticated and unauthenticated chrome.
            <AeriosIQLogo height={28} />
          )}
        </Toolbar>
      </AppBar>
      <Box
        component="main"
        // Invariant accessible name. We previously referenced an in-card
        // `id="login-title"`, but the loading skeleton, UnknownTenantAlert and
        // WrongTenantAlert states do not render that element, leaving the
        // reference dangling for AT users (WCAG 4.1.2). Use a stable label
        // that describes the region's purpose in every state.
        aria-label="Sign in"
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          // Generous vertical breathing room — Apple-clean marketing
          // rhythm centres the card high on the canvas, not glued to
          // the top bar.
          pt: { xs: 4, md: 10 },
          pb: { xs: 4, md: 8 },
          px: { xs: 2, md: 4 },
          // Drop the legacy slate gradient. Canvas is `surface.canvas`
          // (Apple Gray-White #F5F5F7) so the white card floats with
          // a soft tonal contrast.
          backgroundColor: "background.default",
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
